import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { spassuCodeApiRef } from '../../api';
import { useProjectRole } from '../../hooks/useProjectRole';
import type { ComplianceMetrics } from '../../api/types';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 700,
    fontFamily: "'Lexend Peta', sans-serif",
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  statusIcon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
  healthy: { color: '#2E7D32' },
  warning: { color: '#F9A825' },
  critical: { color: '#D32F2F' },
  periodSelector: {
    marginBottom: theme.spacing(2),
    minWidth: 120,
  },
  policyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const POLICY_LABELS: Record<string, string> = {
  min_approvers_per_phase: 'Min. aprovadores por fase',
  require_different_approver: 'Aprovador diferente do criador',
  sla_hours_per_phase: 'SLA (horas) por fase',
};

export interface ComplianceDashboardProps {
  projectKey: string;
}

export const ComplianceDashboard = ({
  projectKey,
}: ComplianceDashboardProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const { hasPermission } = useProjectRole();
  const canEditPolicies = hasPermission(projectKey, 'member.add');
  const [days, setDays] = useState(30);

  const {
    value: metrics,
    loading,
    error,
  } = useAsync(
    () => api.getProjectCompliance(projectKey, days),
    [projectKey, days],
  );

  // Policy editing state
  const [policyValues, setPolicyValues] = useState<Record<string, unknown>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const policyInitialized = useRef(false);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const lastSavedValues = useRef<Record<string, unknown>>({});

  // Sync policy values from metrics on first load only
  useEffect(() => {
    if (metrics?.policies && !policyInitialized.current) {
      setPolicyValues(metrics.policies);
      lastSavedValues.current = { ...metrics.policies };
      policyInitialized.current = true;
    }
  }, [metrics]);

  // Reset on project change
  useEffect(() => {
    policyInitialized.current = false;
  }, [projectKey]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const policySaving = savingKeys.size > 0;

  const handlePolicyChange = useCallback(
    (key: string, value: unknown) => {
      // Update local state immediately
      setPolicyValues(prev => ({ ...prev, [key]: value }));

      // Debounce the API call (500ms)
      if (debounceTimers.current[key]) {
        clearTimeout(debounceTimers.current[key]);
      }
      debounceTimers.current[key] = setTimeout(async () => {
        setSavingKeys(prev => new Set(prev).add(key));
        try {
          await api.setProjectPolicy(projectKey, key, value);
          lastSavedValues.current[key] = value;
        } catch {
          // Revert only this specific key
          setPolicyValues(prev => ({
            ...prev,
            [key]: lastSavedValues.current[key] ?? prev[key],
          }));
        } finally {
          setSavingKeys(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }
      }, 500);
    },
    [api, projectKey],
  );

  if (loading) return <Typography>Carregando métricas...</Typography>;
  if (error)
    return <Typography color="error">Erro: {error.message}</Typography>;
  if (!metrics) return null;

  const healthStatus = (m: ComplianceMetrics) => {
    if (m.activities.errors > 0 || m.members.expired > 0) return 'critical';
    if (m.artifacts.pending > m.artifacts.approved) return 'warning';
    return 'healthy';
  };

  const status = healthStatus(metrics);
  let StatusIcon = ErrorIcon;
  let statusLabel = 'Crítico';
  if (status === 'healthy') {
    StatusIcon = CheckCircleIcon;
    statusLabel = 'OK';
  } else if (status === 'warning') {
    StatusIcon = WarningIcon;
    statusLabel = 'Atenção';
  }

  return (
    <div>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.periodSelector}
      >
        <InputLabel>Período</InputLabel>
        <Select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          label="Período"
        >
          <MenuItem value={7}>7 dias</MenuItem>
          <MenuItem value={30}>30 dias</MenuItem>
          <MenuItem value={90}>90 dias</MenuItem>
        </Select>
      </FormControl>

      <Grid container spacing={2}>
        {/* Health */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography className={classes.metricLabel}>Saúde</Typography>
              <Typography
                className={`${classes.metricValue} ${classes[status]}`}
              >
                <StatusIcon className={classes.statusIcon} />
                {statusLabel}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Activities */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography className={classes.metricLabel}>
                Activities
              </Typography>
              <Typography className={classes.metricValue}>
                {metrics.activities.total}
              </Typography>
              <Typography variant="body2">
                {metrics.activities.active} ativas,{' '}
                {metrics.activities.completed} concluídas
                {metrics.activities.errors > 0 && (
                  <span className={classes.critical}>
                    , {metrics.activities.errors} com erro
                  </span>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Artifacts */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography className={classes.metricLabel}>Artefatos</Typography>
              <Typography className={classes.metricValue}>
                {metrics.artifacts.approved}/{metrics.artifacts.total}
              </Typography>
              <Typography variant="body2">
                {metrics.artifacts.pending} pendentes de aprovação
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Audit Actions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography className={classes.metricLabel}>
                Ações ({days}d)
              </Typography>
              <Typography className={classes.metricValue}>
                {metrics.auditActions}
              </Typography>
              <Typography variant="body2">
                {metrics.members.total} membros ({metrics.members.temporary}{' '}
                temporários)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Policies — editable */}
        <Grid item xs={12} sm={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography className={classes.metricLabel} gutterBottom>
                Políticas do Projeto
              </Typography>

              <div className={classes.policyItem}>
                <Typography variant="body2">
                  {POLICY_LABELS.min_approvers_per_phase}
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  variant="outlined"
                  disabled={!canEditPolicies}
                  value={policyValues.min_approvers_per_phase ?? 1}
                  onChange={e =>
                    handlePolicyChange(
                      'min_approvers_per_phase',
                      Number(e.target.value),
                    )
                  }
                  inputProps={{
                    min: 1,
                    max: 10,
                    style: { width: 60, textAlign: 'center' },
                  }}
                />
              </div>

              <div className={classes.policyItem}>
                <Typography variant="body2">
                  {POLICY_LABELS.require_different_approver}
                </Typography>
                <Switch
                  checked={Boolean(policyValues.require_different_approver)}
                  onChange={e =>
                    handlePolicyChange(
                      'require_different_approver',
                      e.target.checked,
                    )
                  }
                  disabled={!canEditPolicies}
                  color="primary"
                  size="small"
                />
              </div>

              <div className={classes.policyItem}>
                <Typography variant="body2">
                  {POLICY_LABELS.sla_hours_per_phase}
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  variant="outlined"
                  disabled={!canEditPolicies}
                  value={policyValues.sla_hours_per_phase ?? ''}
                  onChange={e =>
                    handlePolicyChange(
                      'sla_hours_per_phase',
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                  placeholder="—"
                  inputProps={{
                    min: 1,
                    max: 8760,
                    style: { width: 70, textAlign: 'center' },
                  }}
                />
              </div>

              {policySaving && (
                <Typography variant="caption" color="textSecondary">
                  Salvando...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};
